<!--
  Copyright (C) 2020 ExploRêve

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see <https://www.gnu.org/licenses/>.

  Full source code is available at <https://gitlab.com/exploreve/frontend>.
-->

<template>
  <div :class="['IM_1_' + this.id]">
    <div class="container">
      <div id="hdp"></div>
      <h1>Rentrons dans la Troposphère...</h1>
      <p>
        Ton foulard est en place, prêt à servir de lien entre ton monde d’origine et la Troposphère. Mais cela n’est pas
        encore suffisant… La plupart des gens qui accèdent à la Troposphère le font par hasard… Mais aujourd’hui, pour
        pouvoir explorêver ensemble, il faut que tu le fasses volontairement.
        <br />
        <br />Pour t’aider à te mettre dans cet état d'esprit si particulier, très proche du rêve éveillé, nous avons
        conçu un mécanisme combinant trois éléments. Mais ton esprit doit les découvrir lui-même !
      </p>
    </div>

    <div class="row">
      <div class="col-md-4">
        <div v-if="!succeed1" class="l1 lamps">
          <img v-bind:src="red" alt />
        </div>
        <div v-if="succeed1" class="l1 lamps">
          <img v-bind:src="green" alt />
        </div>
        <div
          class="show-button"
          v-on:click="(display1 = true), (display2 = false), (display3 = false), emitMusic('play', 3)"
        >
          <a class="show" href="#bdp">Voir l'énigme 1</a>
        </div>
      </div>

      <div class="col-md-4">
        <div v-if="!succeed2" class="l2 lamps">
          <img v-bind:src="red" alt />
        </div>
        <div v-if="succeed2" class="l2 lamps">
          <img v-bind:src="green" alt />
        </div>
        <div
          class="show-button"
          v-on:click="(display1 = false), (display2 = true), (display3 = false), emitMusic('play', 3)"
        >
          <a class="show" href="#bdp">Voir l'énigme 2</a>
        </div>
      </div>

      <div class="col-md-4">
        <div v-if="!succeed3" class="l3 lamps">
          <img v-bind:src="red" alt />
        </div>
        <div v-if="succeed3" class="l3 lamps">
          <img v-bind:src="green" alt />
        </div>
        <div
          class="show-button"
          v-on:click="(display1 = false), (display2 = false), (display3 = true), emitMusic('pause', 3)"
        >
          <a class="show" href="#bdp">Voir l'énigme 3</a>
        </div>
      </div>
    </div>

    <div v-show="display1 && (!succeed1 || !succeed2 || !succeed3)" class="arrow1"></div>

    <div v-show="display1 && (!succeed1 || !succeed2 || !succeed3)" class="enigme row align-items-center">
      <div class="col-7" style="text-align: left;">
        <p>
          Avec une pelle et mon premier on fait des pâtés,
          <br />Mon deuxième est le joli mois du muguet, <br />On met les points sur mon troisième pour lever un
          malentendu, <br />On découpe ma quatrième dans un gâteau, <br />Mon cinquième est au collège, entre l'enfant
          et l'adulte, <br />Mon sixième n'est pas k'propre, <br />Tu rêves pendant mon tout.
          <br />
          <br />Qui suis-je ?
        </p>
      </div>
      <div class="col-5">
        <input v-model="rep1" v-on:keyup.enter="tester1()" class="rep" placeholder="Entrez la réponse" />
        <div v-on:click="tester1()" class="val">
          <a class="show">Valider</a>
        </div>
      </div>
    </div>

    <div v-show="display2 && (!succeed1 || !succeed2 || !succeed3)" class="arrow2"></div>

    <div v-show="display2 && (!succeed1 || !succeed2 || !succeed3)" class="enigme eg2 row align-items-center">
      <div class="col-7" style="text-align: left;">
        <p>
          La Troposphère est un monde aussi vaste que le nôtre… Son immatérialité permet en plus d’élargir
          considérablement le champ de ce qui y est réalisable &nbsp; !
          <br />
          <br />Dans cet univers intangible, tu pourras te déplacer d’un lieu à l’autre par le biais de ta seule pensée…
          et d’un moyen de transport très particulier, indispensable à tes explorations futures ! À toi de trouver de
          quoi il s’agit en reconstituant
          <a class="puzzle" href="https://www.jigsawplanet.com/?rc=play&pid=2c61abdb0bfe" target="_blank"
            >l’image suivante</a
          >, et saisis son nom ici au singulier. N'hésite pas à agrandir l'image et à veiller aux détails !
        </p>
      </div>
      <div class="col-5">
        <input v-model="rep2" class="rep" v-on:keyup.enter="tester2()" placeholder="Entrez la réponse" />
        <div v-on:click="tester2()" class="val">
          <a class="show">Valider</a>
        </div>
      </div>
    </div>

    <div v-show="display3 && (!succeed1 || !succeed2 || !succeed3)" class="arrow3"></div>

    <div v-show="display3 && (!succeed1 || !succeed2 || !succeed3)" class="enigme eg3 row align-items-center">
      <div class="col-7" style="text-align: left;">
        <p>
          Écoute l’histoire de ces quatre nomades, qui jouent un rôle important dans la Troposphère. Puis réfléchis,
          cherche s’il le faut, et dis-nous de qui il s’agit.
        </p>
        <audio class="audio" controls>
          <source src="https://cdn.exploreve.fr/content/voix/les_4_nomades.mp3" type="audio/mpeg" />
        </audio>

        <p>
          Si tu ne peux pas écouter,
          <a target="_blank" href="https://cdn.exploreve.fr/content/transcriptions/enigme_4_nomades.pdf">clique ici</a>
        </p>
      </div>
      <div class="col-5">
        <input v-model="rep3" class="rep" v-on:keyup.enter="tester3()" placeholder="Entrez la réponse" />
        <div v-on:click="tester3(), testSuccess()" class="val">
          <a class="show">Valider</a>
        </div>
      </div>
    </div>
    <div id="bdp"></div>
    <div v-show="succeed1 && succeed2 && succeed3">
      <p>
        Bravo, tu as réussi ! Te voilà prêt·e à entrer dans la Troposphère ! Tu dérives déjà vers ta première
        destination…
      </p>
      <div v-on:click="emitMusic('pause', 3)">
        <NextBtn class="next" :url="'/immersion01/' + (this.id + 1)" text="Entrer dans la troposphère >" />
      </div>
    </div>
    <b-modal v-model="modalShow" id="my-modal">
      Oups, il semblerait que ta réponse ne soit pas la bonne... Réessaye ! Tu peux aussi demander autour de toi, ou
      contacter les autres de ton unité pour s'entraider. Si jamais tu es vraiment bloqué⸱e, tu peux contacter ExploRêve
      sur les réseaux sociaux ou envoyer un mail à
      <a href="mailto:contact@exploreve.fr"> <BIconEnvelope />contact@exploreve.fr </a>
      .
      <template v-slot:modal-footer>
        <div>
          <b-button style="margin-right: 20px;" variant="primary" @click="modalShow = false">Ok</b-button>
        </div>
      </template>
    </b-modal>
    <a href="#hdp" id="linkHsp" />
  </div>
</template>

<script>
// @ is an alias to /src
import NextBtn from "@/components/NextButton";

export default {
  name: "IM_1_3",

  data: function () {
    return {
      id: 3,
      succeed1: false,
      succeed2: false,
      succeed3: false,

      display1: false,
      display2: false,
      display3: false,

      rep1: "",
      rep2: "",
      rep3: "",

      red: "https://cdn.exploreve.fr/content/global/LampeR.svg",
      green: "https://cdn.exploreve.fr/content/global/LampeV.svg",

      modalShow: false,
      fail:
        "Oups, il semblerait que ta réponse ne soit pas la bonne... Réessaye ! Tu peux aussi demander autour de toi, ou contacter les autres de ton unité pour s'entraider. Si jamais tu es vraiment bloqué⸱e, tu peux contacter ExploRêve sur les réseaux sociaux ou envoyer un mail à contact@exploreve.fr en cliquant sur le bouton en bas à droite de l'écran."
    };
  },

  methods: {
    hdp() {
      document.getElementById("linkHsp").click();
    },
    emitMusic: function (action, id) {
      this.$emit("audio", { action: action, id: id });
    },

    testSuccess: function () {
      if (this.succeed1 && this.succeed2 && this.succeed3) {
        this.emitMusic("play", 5);
      }
    },

    tester1: function () {
      let test = this.rep1.toLowerCase() == "sommeil paradoxal";
      this.modalShow = !test;
      this.succeed1 = test;
      if (test) this.hdp();
    },

    tester2: function () {
      let test =
        this.rep2.toLowerCase() == "ballon-songe" ||
        this.rep2.toLowerCase() == "ballon songe" ||
        this.rep2.toLowerCase() == "ballon-songes" ||
        this.rep2.toLowerCase() == "ballon songes";
      this.modalShow = !test;
      this.succeed2 = test;
      if (test) this.hdp();
    },

    tester3: function () {
      let test =
        this.rep3.toLowerCase() == "vent" ||
        this.rep3.toLowerCase() == "le vent" ||
        this.rep3.toLowerCase() == "les vents" ||
        this.rep3.toLowerCase() == "vents";
      this.modalShow = !test;
      this.succeed3 = test;
      if (test) this.hdp();
    }
  },

  components: { NextBtn }
};
</script>

<style scoped lang="scss">
.container {
  text-align: left;
  align-content: left;
  width: 1000px;
}

.a {
  color: white !important;
}

.audio {
  width: 100%;
}

.show {
  color: white !important;
  text-decoration: none !important;
}
.arrow1 {
  content: "";
  position: relative;
  width: 0px;
  height: 0px;
  border-left: 20px solid transparent;
  border-right: 20px solid transparent;

  border-bottom: 30px solid white;
  left: 20%;

  margin-top: 10px;
  margin-bottom: -20px;
}

.arrow2 {
  content: "";
  position: relative;
  width: 0px;
  height: 0px;
  border-left: 20px solid transparent;
  border-right: 20px solid transparent;

  border-bottom: 30px solid white;
  left: 47%;

  margin-bottom: -20px;
}

.arrow3 {
  content: "";
  position: relative;
  width: 0px;
  height: 0px;
  border-left: 20px solid transparent;
  border-right: 20px solid transparent;

  border-bottom: 30px solid white;
  left: 73%;

  margin-bottom: -20px;
}

.val {
  margin-top: 10px;
  padding: 5px 0px;
  border-radius: 5px;
  font-size: 26px;
  color: white;
  background-color: #5e2a57;
  cursor: pointer;
}

.rep {
  width: 100%;
  text-align: center;
  margin-right: 100px;
  background-color: #fbead3;
  placeholder: "Entrez la réponse";
  border-radius: 5px;
  height: 50px;
}

.enigme {
  margin: 0;
  background-color: white;
  border-radius: 10px;
  height: 520px;
  padding: 30px;
}

.row {
  margin: 10px 200px;
}

p {
  font-size: 24px;
}

.lamps {
  img {
    height: 15vh;
  }
}

.show-button {
  margin: 10px 40px -5px 40px;
  padding: 5px 1px;
  position: aboslute;
  border-radius: 5px;
  background-color: #cd8c31;
  color: white;
  font-size: 26px;
  cursor: pointer;
}
</style>
